import type { ScheduleDay } from '@vnetwork/ui/data';

/**
 * Week / roster helpers shared by the scheduling widgets. Pure date math + a
 * stable token palette so an employee keeps one accent color across cells and
 * the legend.
 */

/** Token palette cycled per distinct employee (dark-mode safe). */
export const MEMBER_PALETTE = [
  'var(--vn-primary-solid)',
  'var(--vn-success-solid)',
  'var(--vn-warning-solid)',
  'var(--vn-info-solid)',
  'var(--vn-danger-solid)',
  'var(--vn-accent-solid)',
] as const;

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Local "YYYY-MM-DD" (no UTC shift — matches the API's date-only keys). */
export function toDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/** Monday of the week containing `ref` (offset in weeks from that week). */
export function mondayOf(ref: Date, weekOffset = 0): Date {
  const d = new Date(ref);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay(); // 0=Sun … 6=Sat
  const diff = (dow === 0 ? -6 : 1) - dow; // shift back to Monday
  d.setDate(d.getDate() + diff + weekOffset * 7);
  return d;
}

/** Seven `ScheduleDay` columns (Mon–Sun) starting at `monday`, today marked. */
export function weekDays(monday: Date): ScheduleDay[] {
  const todayKey = toDateKey(new Date());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    const key = toDateKey(d);
    return {
      key,
      label: DAY_LABELS[d.getDay()],
      sublabel: String(d.getDate()),
      isToday: key === todayKey,
    };
  });
}

/** Short human range for the header, e.g. "Jun 9 – 15". */
export function weekRangeLabel(monday: Date): string {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const mon = monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const sameMonth = monday.getMonth() === sunday.getMonth();
  const sun = sunday.toLocaleDateString(
    'en-US',
    sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' },
  );
  return `${mon} – ${sun}`;
}

/**
 * Assign each employee id a stable palette color. Deterministic within a given
 * set of ids (sorted first) so colors don't reshuffle between renders.
 */
export function buildMemberColors(ids: number[]): Map<number, string> {
  const unique = Array.from(new Set(ids)).sort((a, b) => a - b);
  const map = new Map<number, string>();
  unique.forEach((id, i) => map.set(id, MEMBER_PALETTE[i % MEMBER_PALETTE.length]));
  return map;
}
